import axios from 'axios';
import axiosRetry from 'axios-retry';
import { Auth } from 'aws-amplify';
import Cookies from 'js-cookie';
import { keyBy } from 'lodash-es';
import { trackEvent } from '../../services/analytics-service';

const ID_TOKEN_COOKIE = 'idToken';
export const BASE_API_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true,
  headers: { 'X-Version': import.meta.env.VITE_CODE_VERSION || 'localhost' },
});

axiosRetry(apiClient, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (err) => axiosRetry.isNetworkOrIdempotentRequestError(err),
});

export const axiosInterceptors = {
  request: async (request) => {
    if (!Cookies.get(ID_TOKEN_COOKIE)) await setAuthCookie();
    request.headers['X-Location'] = window.location.href;
    return request;
  },

  response: {
    fulfilled: async (response) => response,
    rejected: (client) => async (error) => {
      const request = error.config;

      if (error.response?.status === 401 && !request.cookieRefreshed) {
        await setAuthCookie();
        request.cookieRefreshed = true;
        return client(request);
      }

      return Promise.reject(error);
    },
  },
};

apiClient.interceptors.request.use(axiosInterceptors.request);
apiClient.interceptors.response.use(
  axiosInterceptors.response.fulfilled,
  axiosInterceptors.response.rejected(apiClient),
);

async function setAuthCookie() {
  try {
    const session = await Auth.currentSession();
    Cookies.set(ID_TOKEN_COOKIE, session.getIdToken().getJwtToken());
  } catch (e) {
    // not logged in
  }
}

export async function login() {
  const currentUser = await get('/current-user');

  trackEvent({
    name: 'set_user_type',
    category: 'User',
    label: 'User Type',
    action: 'login',
    value: currentUser.role.toUpperCase(),
  });

  return currentUser;
}

export async function logout() {
  Cookies.remove(ID_TOKEN_COOKIE);
  await Auth.signOut();
  window.location = '/';
}

export function get(path, options) {
  return apiClient.get(path, options).then(handleResponse).catch(handleError);
}

export function getRaw(path, options) {
  return apiClient.get(path, options);
}

export function post(path, payload, options) {
  return apiClient
    .post(path, payload, options)
    .then(handleResponse)
    .catch(handleError);
}

export function postRaw(path, payload, options) {
  return apiClient.post(path, payload, options);
}

export function patch(path, payload) {
  return apiClient.patch(path, payload).then(handleResponse).catch(handleError);
}

export function put(path, payload) {
  return apiClient.put(path, payload).then(handleResponse).catch(handleError);
}

export function destroy(path, payload) {
  return apiClient
    .delete(path, { data: payload })
    .then(handleResponse)
    .catch(handleError);
}

function handleResponse(response) {
  return response?.data;
}

function handleError(error) {
  const body = error?.response?.data;
  const isValidationError = error.status === 400 && Array.isArray(body);

  if (isValidationError) {
    error.validationErrors = keyBy(body, 'attribute');
  }

  throw error;
}

export function download(path, aaids = []) {
  window.location = `${BASE_API_URL}${path}?a=${aaids.join(',')}`;
}

export async function downloadBlob(path, aaids, filters = {}) {
  const filterString = Object.entries(filters)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  const qs = aaids
    ? `?a=${aaids.join(',')}${filterString ? `&${filterString}` : ''}`
    : '';
  return apiClient.get(`${path}${qs}`, { responseType: 'blob' });
}
